import { memo } from "react";
import { API, PRIORITY_STYLES } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from "@/components/ui/context-menu";
import { Paperclip, Clock, Send, Forward, Trash2, ReplyAll, MailOpen, Mail, Building2, Sparkles } from "lucide-react";
import { SwipeRow } from "./SwipeRow";
import { avatarFor, fmtDate } from "./inboxUtils";

const HL = ({ text, re }) => {
  const s = text == null ? "" : String(text);
  if (!re || !s) return s || null;
  return s.split(re).map((p, i) => (i % 2 === 1
    ? <mark key={i} className="bg-yellow-200 text-inherit font-semibold rounded-[2px] px-0.5">{p}</mark>
    : p));
};


const EmailListItemInner = ({
  it, i, isSelected, isChecked, isInbox, selectMode, activeFolder, threadCount, threadPosition, threadReceivedTotal, leaving,
  catStyle, catLabel, hlRe, onSelect, onToggleChecked, onOpenThread, onReply, onReplyAll, onForward, onDelete, onToggleReadItem,
}) => {
  const nm = activeFolder === "sent" ? (it.recipient || "?") : (it.sender_name || it.sender || "?");
  const { initial, color } = avatarFor(nm);
  const objs = (it.attachments || []).map((a) => (typeof a === "string" ? { filename: a } : a));
  const th = objs.find((a) => a.has_thumb && a.file_id);
  // Progressivo della risposta ricevuta (la più vecchia = 1). Fallback al conteggio totale del thread.
  const useProgressive = threadPosition && threadReceivedTotal > 1;
  const badgeNum = useProgressive ? threadPosition : threadCount;
  const badgeTitle = useProgressive
    ? `Risposta ${threadPosition} di ${threadReceivedTotal} — apri la conversazione`
    : `${threadCount} messaggi — apri la conversazione`;
  const showBadge = useProgressive || threadCount > 1;
  return (
    <SwipeRow it={it} isInbox={isInbox && !selectMode} onDelete={onDelete} onToggleRead={onToggleReadItem}
      className={`mail-card ${isSelected ? "mail-card--selected " : ""}${(!it.is_read && isInbox) ? "mail-card--unread " : ""}${leaving ? "row-leaving" : "list-enter"} rounded-xl mb-2 transition-shadow duration-150 ${
        isSelected ? "bg-[#EBF3FF] ring-1 ring-[#007BFF]/50 shadow-md"
        : (!it.is_read && isInbox) ? "bg-white ring-1 ring-[#007BFF]/15 shadow-sm hover:shadow-md"
        : "bg-white shadow-sm hover:shadow-md"}`}
      style={leaving ? undefined : { animationDelay: `${Math.min(i, 12) * 25}ms` }}>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <div className="relative w-full flex items-stretch">
            {selectMode && (
              <div className="flex items-center pl-4" onClick={(e) => { e.stopPropagation(); onToggleChecked(it.id); }}>
                <Checkbox data-testid={`email-checkbox-${i}`} checked={isChecked} className="pointer-events-none" />
              </div>
            )}
            <button data-testid={`email-list-item-${i}`} onClick={() => (selectMode ? onToggleChecked(it.id) : onSelect(it))}
              className="flex-1 min-w-0 text-left px-4 py-3.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1 min-w-0">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border shrink-0 ${catStyle(it.category)}`}>{catLabel(it.category) || it.category}</span>
                  {it.condomino_building && (
                    <span data-testid={`condomino-building-${i}`} title={`Mittente registrato in anagrafica: ${it.condomino_building}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 shrink-0 max-w-[140px] truncate">
                      <Building2 className="h-3 w-3 shrink-0" /><span className="truncate">{it.condomino_building.replace(/^\s*condominio\s+/i, "")}</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {it.ai_score != null && (
                    <span data-testid={`ai-score-${i}`} title={`Pertinenza semantica: ${it.ai_score}%`}
                      className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full border shrink-0 ${
                        it.ai_score >= 60 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                        : it.ai_score >= 40 ? "bg-[#EBF3FF] text-[#0056B3] border-[#007BFF]/20"
                        : "bg-gray-100 text-gray-500 border-gray-200"}`}>
                      <Sparkles className="h-2.5 w-2.5" />{it.ai_score}%
                    </span>
                  )}
                  {it.match_in_attachment && (
                    <span data-testid={`match-attachment-${i}`} title="La corrispondenza è nel testo dell'allegato"
                      className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                      <Paperclip className="h-3 w-3" />match nell'allegato
                    </span>
                  )}
                  {it.has_attachments && (
                    it.attachments_pending
                      ? <Clock data-testid={`attachment-pending-icon-${i}`} className="h-3.5 w-3.5 text-amber-500 animate-pulse" title="Allegati in arrivo — scaricati all'apertura della mail" />
                      : <Paperclip data-testid={`attachment-icon-${i}`} className="h-3.5 w-3.5 text-gray-400" title="Contiene allegati" />
                  )}
                  {isInbox && <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${PRIORITY_STYLES[it.priority]}`}>{it.priority}</span>}
                </div>
              </div>
              <div className="flex gap-2.5">
                <div className="relative shrink-0 self-start" data-testid={`avatar-${i}`}>
                  <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold text-sm select-none" style={{ backgroundColor: color }}>
                    {initial}
                  </div>
                  {showBadge && (
                    <span role="button" tabIndex={0} data-testid={`thread-count-${i}`} title={badgeTitle}
                      onClick={(e) => { e.stopPropagation(); onOpenThread(it); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onOpenThread(it); } }}
                      className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 rounded-full bg-gray-800 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white cursor-pointer hover:bg-[#007BFF] transition-colors duration-150">
                      {badgeNum}
                    </span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {!it.is_read && isInbox && <span className="h-2.5 w-2.5 rounded-full bg-[#007BFF] shrink-0 ring-2 ring-[#007BFF]/20" title="Non letta" />}
                    <div className={`text-sm truncate ${!it.is_read && isInbox ? "font-bold text-gray-900" : "font-medium text-gray-800"}`}><HL text={it.subject} re={hlRe} /></div>
                  </div>
                  <div className="text-xs text-gray-500 truncate mt-0.5">{activeFolder === "sent" ? <>A: {it.recipient || "—"}</> : <HL text={it.sender_name} re={hlRe} />}</div>
                  <div className="text-xs text-gray-400 truncate mt-0.5"><HL text={it.summary || ""} re={hlRe} /></div>
                  <div data-testid={`email-date-${i}`} className="text-[10px] text-gray-400 mt-1 flex items-center gap-1"><Clock className="h-3 w-3" />{fmtDate(it.email_date || it.created_at)}</div>
                </div>
                {th ? <img src={`${API}/attachments/${th.file_id}/thumb`} alt="" loading="lazy" data-testid={`list-thumb-${i}`} className="h-16 w-12 object-cover object-top rounded border border-gray-200 shrink-0 bg-gray-50" /> : null}
              </div>
            </button>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          <ContextMenuItem data-testid={`ctx-reply-${i}`} onClick={() => onReply(it)}>
            <Send className="h-4 w-4 mr-2" />Rispondi
          </ContextMenuItem>
          <ContextMenuItem data-testid={`ctx-reply-all-${i}`} onClick={() => onReplyAll(it)}>
            <ReplyAll className="h-4 w-4 mr-2" />Rispondi a tutti
          </ContextMenuItem>
          <ContextMenuItem data-testid={`ctx-forward-${i}`} onClick={() => onForward(it)}>
            <Forward className="h-4 w-4 mr-2" />Inoltra
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem data-testid={`ctx-toggle-read-${i}`} onClick={() => onToggleReadItem(it)}>
            {it.is_read
              ? <><Mail className="h-4 w-4 mr-2" />Segna come da leggere</>
              : <><MailOpen className="h-4 w-4 mr-2" />Segna come letto</>}
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem data-testid={`ctx-delete-${i}`} onClick={() => onDelete(it)} className="text-red-600 focus:text-red-700">
            <Trash2 className="h-4 w-4 mr-2" />Elimina
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
    </SwipeRow>
  );
};

export const EmailListItem = memo(EmailListItemInner);
